import React, { useState } from 'react';
import { Product } from '../types';
import { useLocation } from 'wouter';
import { Search, ShoppingCart } from 'lucide-react';
import QuickBuyModal from './QuickBuyModal';
import QuickViewModal from './QuickViewModal';

interface ProductCardProps {
  product: Product;
  onOpenCart?: () => void;
}

export default function ProductCard({ product, onOpenCart }: ProductCardProps) {
  const [, setLocation] = useLocation();
  const [isQuickBuyOpen, setIsQuickBuyOpen] = useState(false);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);

  const isOutOfStock = product.isOutOfStock === true || product.stock === 0;

  // Format price strictly as Rs. 58.00
  const formattedPrice = `Rs. ${(product.price || 0).toFixed(2)}`;

  const handleCardClick = () => {
    setLocation(`/product/${product.id}`);
  };

  const handleQuickViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsQuickViewOpen(true);
  };

  const handleQuickBuyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsQuickBuyOpen(true);
  };

  return (
    <>
      <div
        onClick={handleCardClick}
        className="group relative bg-white flex flex-col justify-between cursor-pointer border-b border-r border-[#E5E0D8] p-3 sm:p-4 transition-all duration-300 hover:shadow-sm"
      >
        {/* Product Image Area */}
        <div className="relative aspect-square w-full bg-white flex items-center justify-center overflow-hidden mb-3">
          {/* Sold out pill badge at top-left */}
          {isOutOfStock && (
            <div className="absolute top-1.5 left-1.5 z-10">
              <span className="badge-sold-out text-[9px] sm:text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider shadow-sm">
                Sold out
              </span>
            </div>
          )}

          {/* Product Image */}
          <img
            src={product.image}
            alt={product.name}
            referrerPolicy="no-referrer"
            className="w-full h-full object-contain p-2 transition-transform duration-500 ease-out group-hover:scale-105"
            loading="lazy"
          />

          {/* Floating Action Buttons over bottom-right of image */}
          <div className="absolute bottom-1.5 right-1.5 z-10 flex items-center gap-1.5">
            {/* Magnifier / Quick View */}
            <button
              type="button"
              onClick={handleQuickViewClick}
              aria-label={`Quick view ${product.name}`}
              className="card-action-btn w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            >
              <Search className="w-3.5 h-3.5 sm:w-4 sm:h-4 stroke-[2.2]" />
            </button>

            {/* Shopping Cart / Quick Buy */}
            <button
              type="button"
              onClick={handleQuickBuyClick}
              disabled={isOutOfStock}
              aria-label={`Quick buy ${product.name}`}
              className={`card-action-btn w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center active:scale-90 transition-transform ${
                isOutOfStock ? 'opacity-40 cursor-not-allowed' : ''
              }`}
            >
              <ShoppingCart className="w-3.5 h-3.5 sm:w-4 sm:h-4 stroke-[2.2]" />
            </button>
          </div>
        </div>

        {/* Product Title & Price (Centered) */}
        <div className="flex flex-col items-center text-center px-1">
          <h3 className="text-xs sm:text-sm font-bold text-[#1F1F1F] leading-snug line-clamp-2 mb-1.5 group-hover:text-[#D97736] transition-colors">
            {product.name}
          </h3>

          <div className="text-xs sm:text-sm font-bold text-[#1F1F1F]">
            {formattedPrice}
          </div>
        </div>
      </div>

      {/* Quick Buy Bottom Sheet Modal */}
      <QuickBuyModal
        product={product}
        isOpen={isQuickBuyOpen}
        onClose={() => setIsQuickBuyOpen(false)}
        onOpenCart={onOpenCart}
      />

      {/* Quick View Zoom Modal */}
      <QuickViewModal
        product={product}
        isOpen={isQuickViewOpen}
        onClose={() => setIsQuickViewOpen(false)}
        onOpenQuickBuy={() => {
          setIsQuickViewOpen(false);
          setIsQuickBuyOpen(true);
        }}
      />
    </>
  );
}
